import {
  SKIP_REASON_LABELS,
  SKIP_REASONS,
  type SkipReason,
} from '@fundline/shared/campaigns/eligibility';
import {
  BUILT_IN_FIELDS,
  BUILT_IN_LABELS,
  checkTemplate,
  DEFAULT_FALLBACKS,
  sampleText,
} from '@fundline/shared/campaigns/template';
import { formatCentsAmount } from '@fundline/shared/money';
import { describeReason, statusLabel, type MessageStatus } from '@fundline/shared/messages/status';
import type { CampaignDraft, CampaignPreview } from '@fundline/shared/schemas/campaigns';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useId, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL, SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { useComplianceSettingsQuery } from '../compliance/use-compliance-settings.ts';
import { useNow } from '../compliance/use-sending-rules.ts';
import { fetchMerchantPage } from '../merchants/use-merchants.ts';
import { useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { useWorkspaceQuery } from '../settings/use-workspace.ts';
import { AudienceBuilder } from './AudienceBuilder.tsx';
import { CAMPAIGN_STATUS } from './CampaignsPage.tsx';
import {
  type CampaignSummary,
  RECIPIENT_PAGE,
  useCampaignActions,
  useCampaignPreviewQuery,
  useCampaignQuery,
  useCampaignStatsQuery,
  useRecipientsQuery,
} from './use-campaigns.ts';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const n = (v: number) => v.toLocaleString('en-US');

/** One campaign: the editor while it is a draft, its progress once it has launched. */
export function CampaignPage({ campaignId }: { campaignId: string }) {
  const { workspaceId } = useWorkspaceClaims();
  const campaign = useCampaignQuery(workspaceId, campaignId);
  return (
    <div className="flex flex-col gap-5">
      {campaign.isPending ? <Spinner label="Loading the campaign" /> : null}
      {campaign.isError ? <Alert tone="error">{campaign.error.message}</Alert> : null}
      {campaign.data?.status === 'draft' ? (
        <CampaignEditor key={campaign.data.id} campaign={campaign.data} />
      ) : null}
      {campaign.data && campaign.data.status !== 'draft' ? (
        <CampaignProgress campaign={campaign.data} />
      ) : null}
    </div>
  );
}

function Back() {
  return (
    <Link
      to="/campaigns"
      className="fl-btn fl-btn--ghost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
    >
      All campaigns
    </Link>
  );
}

// ─────────────────────────── The draft editor ───────────────────────────

function toDraft(c: CampaignSummary): CampaignDraft {
  return {
    name: c.name,
    audience: c.audience,
    template: c.template,
    maxSegments: c.maxSegments,
    frequencyDays: c.frequencyDays,
    aiEnabled: c.aiEnabled,
    aiModel: c.aiModel,
  };
}

function CampaignEditor({ campaign }: { campaign: CampaignSummary }) {
  const { workspaceId } = useWorkspaceClaims();
  const navigate = useNavigate();
  const actions = useCampaignActions(workspaceId, campaign.id);
  const workspace = useWorkspaceQuery(workspaceId);
  const compliance = useComplianceSettingsQuery(workspaceId);
  const fields = useFieldDefinitionsQuery(workspaceId);
  const [draft, setDraft] = useState<CampaignDraft>(() => toDraft(campaign));
  const [dirty, setDirty] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useCampaignPreviewQuery(workspaceId, campaign.id, reviewing);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const insertId = useId();
  const textId = useId();

  const workspaceName = workspace.data?.name ?? '';
  const footer = compliance.data?.form.optOutFooter ?? 'Reply STOP to opt out';
  const builtIn = new Set<string>(BUILT_IN_FIELDS);
  const customFields = (fields.data ?? [])
    .filter((f) => !f.archivedAt && (f.type === 'text' || f.type === 'dropdown'))
    .filter((f) => !builtIn.has(f.key))
    .map((f) => ({ key: f.key, label: f.label }));
  const issues = workspaceName
    ? checkTemplate(draft.template, { workspaceName, customFields })
    : [];
  const sample = sampleText(draft.template, workspaceName || 'Your company', footer);
  const unicode = sample.info.encoding === 'ucs2';

  const set = <K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
    setReviewing(false);
  };

  async function save(): Promise<boolean> {
    setError(null);
    try {
      await actions.save.mutateAsync(draft);
      setDirty(false);
      return true;
    } catch (err) {
      setError(messageOf(err));
      return false;
    }
  }

  async function review() {
    if (dirty && !(await save())) return;
    setReviewing(true);
    await preview.refetch();
  }

  function insertField(key: string) {
    if (!key) return;
    const tag =
      key === 'company'
        ? '{company}'
        : `{${key}|${key in DEFAULT_FALLBACKS ? DEFAULT_FALLBACKS[key as keyof typeof DEFAULT_FALLBACKS] : 'there'}}`;
    const el = textArea.current;
    const at = el?.selectionStart ?? draft.template.length;
    const next = `${draft.template.slice(0, at)}${tag}${draft.template.slice(el?.selectionEnd ?? at)}`;
    set('template', next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + tag.length, at + tag.length);
    });
  }

  async function discard() {
    if (!window.confirm('Discard this draft? It moves to cancelled; nothing was sent.')) return;
    try {
      await actions.cancel.mutateAsync();
      await navigate({ to: '/campaigns' });
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <>
      <PageHeader
        title={draft.name || 'New campaign'}
        description="Draft. Nothing goes out until you launch it."
        actions={<Back />}
      />
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card title="Name">
        <TextField
          label="Campaign name"
          hint="Only your team sees it."
          value={draft.name}
          maxLength={80}
          onChange={(e) => set('name', e.target.value)}
        />
      </Card>

      <Card title="Who gets it">
        <AudienceBuilder
          workspaceId={workspaceId}
          value={draft.audience}
          onChange={(audience) => set('audience', audience)}
        />
      </Card>

      <Card title="Message">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor={insertId} className="callout font-semibold text-ink">
                Insert field
              </label>
              <select
                id={insertId}
                className={`${CONTROL} w-auto`}
                value=""
                onChange={(e) => insertField(e.target.value)}
              >
                <option value="">Choose a field…</option>
                {BUILT_IN_FIELDS.map((k) => (
                  <option key={k} value={k}>
                    {BUILT_IN_LABELS[k]}
                  </option>
                ))}
                {customFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={textId} className="callout font-semibold text-ink">
              Text
            </label>
            <textarea
              id={textId}
              ref={textArea}
              rows={5}
              className={`${CONTROL} py-2`}
              value={draft.template}
              onChange={(e) => set('template', e.target.value)}
              aria-describedby={`${textId}-hint`}
              data-testid="campaign-template"
            />
            <p id={`${textId}-hint`} className="footnote text-ink-muted">
              Merge fields look like {'{first_name|there}'}: the part after | is used when a
              merchant has no value. Say who&apos;s texting with {'{company}'}.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="callout font-semibold text-ink">What a merchant with no values gets</p>
            <div
              className="fl-bubble fl-bubble--auto self-start whitespace-pre-wrap"
              data-testid="campaign-sample"
            >
              {sample.text}
            </div>
            <p
              className={`footnote ${sample.info.segments > draft.maxSegments || unicode ? 'text-danger' : 'text-ink-muted'}`}
              data-testid="campaign-segments"
            >
              {sample.info.segments} {sample.info.segments === 1 ? 'segment' : 'segments'} ·{' '}
              {sample.info.characters} characters, opt-out line included
              {unicode
                ? ` · Unicode because of ${sample.info.nonGsmCharacters.map((c) => `"${c}"`).join(', ')}: each segment holds 70 characters instead of 160`
                : ''}
            </p>
            <p className="footnote text-ink-muted">
              The opt-out line is added to every campaign text. Merchants' own values can make some
              texts longer; the review shows the longest.
            </p>
          </div>
          {issues.length > 0 ? (
            <Alert tone="warning">
              <ul className="list-disc pl-5" data-testid="template-issues">
                {issues.map((i) => (
                  <li key={i.message}>{i.message}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </div>
      </Card>

      <Card title="Options">
        <div className="flex flex-col gap-3">
          <SelectField
            label="Longest text allowed"
            hint="Texts longer than this are skipped. More than one segment costs more and needs your confirmation at launch."
            value={String(draft.maxSegments)}
            onChange={(e) => set('maxSegments', Number(e.target.value))}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => ({
              value: String(v),
              label: v === 1 ? '1 segment (recommended)' : `${v} segments`,
            }))}
          />
          <SelectField
            label="Skip merchants who got a campaign text in the last"
            value={String(draft.frequencyDays)}
            onChange={(e) => set('frequencyDays', Number(e.target.value))}
            options={[7, 6, 5, 4, 3, 2, 1, 0].map((v) => ({
              value: String(v),
              label: v === 0 ? "Don't skip anyone for this" : `${v} ${v === 1 ? 'day' : 'days'}`,
            }))}
          />
          <label className="callout flex min-h-11 items-center gap-2 text-ink">
            <input
              type="checkbox"
              className="size-4 accent-brand"
              checked={draft.aiEnabled}
              onChange={(e) => set('aiEnabled', e.target.checked)}
            />
            AI auto-reply to answers (starts working in a later update)
          </label>
          {draft.aiEnabled ? (
            <SelectField
              label="AI model"
              value={draft.aiModel}
              onChange={(e) => set('aiModel', e.target.value as CampaignDraft['aiModel'])}
              options={[
                { value: 'haiku', label: 'Claude Haiku (default)' },
                { value: 'sonnet', label: 'Claude Sonnet (better replies, costs more)' },
              ]}
            />
          ) : null}
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          busy={actions.save.isPending}
          disabled={!dirty}
          onClick={() => void save()}
        >
          {dirty ? 'Save draft' : 'Saved'}
        </Button>
        <Button busy={preview.isFetching} onClick={() => void review()}>
          Review
        </Button>
        <Button variant="ghost" onClick={() => void discard()}>
          Discard draft
        </Button>
      </div>

      {reviewing && preview.isError ? <Alert tone="error">{preview.error.message}</Alert> : null}
      {reviewing && preview.data && !dirty ? (
        <Review campaign={campaign} draft={draft} preview={preview.data} />
      ) : null}
    </>
  );
}

function Stat({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className="rounded-md bg-surface px-3 py-2 ring-1 ring-hairline" data-testid={testId}>
      <p className="footnote text-ink-muted">{label}</p>
      <p className="title fl-num text-ink">{value}</p>
    </div>
  );
}

function SkipList({ counts }: { counts: Record<SkipReason, number> }) {
  const shown = SKIP_REASONS.filter((r) => counts[r] > 0);
  if (shown.length === 0) return null;
  return (
    <ul className="callout flex flex-col gap-1 text-ink" data-testid="skip-reasons">
      {shown.map((r) => (
        <li key={r} className="flex justify-between gap-3">
          <span>{SKIP_REASON_LABELS[r]}</span>
          <span className="fl-num text-ink-muted">{n(counts[r])}</span>
        </li>
      ))}
    </ul>
  );
}

/** The review (M9 #2): who gets it, who is skipped, cost, timing, problems; then test, schedule, launch. */
function Review({
  campaign,
  draft,
  preview,
}: {
  campaign: CampaignSummary;
  draft: CampaignDraft;
  preview: CampaignPreview;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const actions = useCampaignActions(workspaceId, campaign.id);
  const [confirmSegments, setConfirmSegments] = useState(false);
  const [launchKey] = useState(() => crypto.randomUUID());
  const [scheduleAt, setScheduleAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scheduleId = useId();
  const now = useNow();
  const skipped = SKIP_REASONS.reduce((sum, r) => sum + preview.skipped[r], 0);
  const canGo = preview.blockers.length === 0;
  const needsConfirm = draft.maxSegments > 1;

  async function launch() {
    setError(null);
    try {
      await actions.launch.mutateAsync({ launchKey, confirmMultiSegment: confirmSegments });
    } catch (err) {
      setError(messageOf(err));
    }
  }

  async function schedule() {
    setError(null);
    const at = new Date(scheduleAt);
    if (Number.isNaN(at.getTime())) {
      setError('Pick a date and time.');
      return;
    }
    try {
      await actions.schedule.mutateAsync({
        at: at.toISOString(),
        confirmMultiSegment: confirmSegments,
      });
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <Card title="Review">
      <div className="flex flex-col gap-4" data-testid="campaign-review">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Match" value={n(preview.matched)} testId="review-matched" />
          <Stat label="Get a text" value={n(preview.eligible)} testId="review-eligible" />
          <Stat label="Skipped" value={n(skipped)} testId="review-skipped" />
          <Stat
            label="Estimate"
            value={formatCentsAmount(preview.estimateCents)}
            testId="review-cost"
          />
        </div>
        <SkipList counts={preview.skipped} />
        <ul className="callout flex flex-col gap-1 text-ink-muted">
          <li>
            {n(preview.segmentsTotal)} segments in all; the longest text is{' '}
            {preview.maxSegmentsSeen} {preview.maxSegmentsSeen === 1 ? 'segment' : 'segments'}.
            {preview.usesUnicode ? ' Some texts use Unicode and cost more.' : ''}
          </li>
          <li>
            Estimate {formatCentsAmount(preview.estimateCents)} at about 0.7¢ a segment (your limit
            is {formatCentsAmount(preview.spendLimitCents)} per campaign).
          </li>
          {preview.firstSendAt ? (
            <li>
              First texts go out{' '}
              {new Date(preview.firstSendAt).getTime() <= now.getTime() + 60_000
                ? 'as soon as you launch'
                : `at ${when(preview.firstSendAt)} (your time)`}
              , each in the merchant's own sending hours.
            </li>
          ) : null}
          {preview.sendingDays !== null && preview.eligible > 0 ? (
            <li>
              About {preview.sendingDays} sending {preview.sendingDays === 1 ? 'day' : 'days'} at
              your numbers' daily limits. Texts not out after 3 sending days are dropped.
            </li>
          ) : null}
        </ul>
        {preview.samples.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="callout font-semibold text-ink">Examples</p>
            {preview.samples.map((s) => (
              <div key={s.merchantId} className="flex flex-col gap-1">
                <p className="footnote text-ink-muted">
                  {s.label} · {s.segments} {s.segments === 1 ? 'segment' : 'segments'}
                </p>
                <div className="fl-bubble fl-bubble--auto self-start whitespace-pre-wrap">
                  {s.text}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {preview.blockers.length > 0 ? (
          <Alert tone="error">
            <p className="font-semibold">Fix these before it can go:</p>
            <ul className="list-disc pl-5" data-testid="review-blockers">
              {preview.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <TestSend campaignId={campaign.id} />

        {canGo ? (
          <div className="flex flex-col gap-3 border-t border-hairline pt-4">
            {needsConfirm ? (
              <label className="callout flex items-start gap-2 text-ink">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-brand"
                  checked={confirmSegments}
                  onChange={(e) => setConfirmSegments(e.target.checked)}
                />
                I understand a text can be up to {draft.maxSegments} segments and cost up to{' '}
                {draft.maxSegments} times as much. This is recorded.
              </label>
            ) : null}
            {error ? <Alert tone="error">{error}</Alert> : null}
            <div className="flex flex-wrap items-end gap-2">
              <Button
                busy={actions.launch.isPending}
                disabled={needsConfirm && !confirmSegments}
                onClick={() => void launch()}
                data-testid="launch-campaign"
              >
                Launch to {n(preview.eligible)} {preview.eligible === 1 ? 'merchant' : 'merchants'}
              </Button>
              <span className="callout text-ink-muted">or</span>
              <div className="flex flex-col gap-1">
                <label htmlFor={scheduleId} className="footnote text-ink-muted">
                  Schedule for (your time)
                </label>
                <input
                  id={scheduleId}
                  type="datetime-local"
                  className={`${CONTROL} w-auto`}
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                busy={actions.schedule.isPending}
                disabled={!scheduleAt || (needsConfirm && !confirmSegments)}
                onClick={() => void schedule()}
              >
                Schedule
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** A test text to one merchant, through the same rules as everyone else (#29). */
function TestSend({ campaignId }: { campaignId: string }) {
  const { workspaceId } = useWorkspaceClaims();
  const actions = useCampaignActions(workspaceId, campaignId);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; label: string }[]>([]);
  const [chosen, setChosen] = useState<{ id: string; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentId, setSentId] = useState<string | null>(null);
  const searchId = useId();

  async function search(text: string) {
    setQ(text);
    setChosen(null);
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      const page = await fetchMerchantPage(
        workspaceId,
        { q: text, rep: null, tag: null, archived: false },
        0,
      );
      setResults(
        page.slice(0, 6).map((m) => ({
          id: m.id,
          label: m.businessName?.trim() || m.ownerName?.trim() || m.phone,
        })),
      );
    } catch (err) {
      setError(messageOf(err));
    }
  }

  async function send() {
    if (!chosen) return;
    setError(null);
    setSentId(null);
    try {
      await actions.test.mutateAsync({ merchantId: chosen.id, clientKey: crypto.randomUUID() });
      setSentId(chosen.id);
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-hairline pt-4" data-testid="test-send">
      <p className="callout font-semibold text-ink">Send a test</p>
      <p className="footnote text-ink-muted">
        To one merchant (for example, one with your own number). It follows the same rules as the
        campaign, and up to 5 a day.
      </p>
      <label htmlFor={searchId} className="sr-only">
        Find a merchant
      </label>
      <input
        id={searchId}
        className={CONTROL}
        placeholder="Search name, business or number"
        value={q}
        onChange={(e) => void search(e.target.value)}
      />
      {results.length > 0 && !chosen ? (
        <ul className="flex flex-col rounded-md bg-surface ring-1 ring-hairline">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="callout min-h-11 w-full px-3 text-left text-ink hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-focus-ring"
                onClick={() => {
                  setChosen(r);
                  setQ(r.label);
                }}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!chosen}
          busy={actions.test.isPending}
          onClick={() => void send()}
        >
          Send test{chosen ? ` to ${chosen.label}` : ''}
        </Button>
      </div>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {sentId ? (
        <Alert tone="success">
          Test queued.{' '}
          <Link to="/inbox/$merchantId" params={{ merchantId: sentId }} className="underline">
            Open the conversation
          </Link>
        </Alert>
      ) : null}
    </div>
  );
}

// ─────────────────────────── After launch ───────────────────────────

const PAUSE_REASONS: Record<string, string> = {
  admin: 'Paused by an admin.',
  pause_all: 'Paused with every campaign (Pause all).',
  failures:
    'Paused itself: more than 15% of its texts failed. Check the failures, then resume or cancel it.',
  opt_outs:
    'Paused itself: 5% or more of the merchants opted out. Check the message, then resume or cancel it.',
};

function CampaignProgress({ campaign }: { campaign: CampaignSummary }) {
  const { workspaceId } = useWorkspaceClaims();
  const navigate = useNavigate();
  const live = campaign.status === 'running' || campaign.status === 'paused';
  const stats = useCampaignStatsQuery(workspaceId, campaign.id, live);
  const actions = useCampaignActions(workspaceId, campaign.id);
  const [error, setError] = useState<string | null>(null);
  const s = stats.data;
  const status = CAMPAIGN_STATUS[campaign.status];

  async function run(action: 'pause' | 'resume' | 'cancel' | 'unschedule') {
    if (
      action === 'cancel' &&
      !window.confirm(
        'Cancel this campaign? Texts not sent yet will never go out. Texts already with the carrier still arrive.',
      )
    ) {
      return;
    }
    setError(null);
    try {
      await actions[action].mutateAsync();
    } catch (err) {
      setError(messageOf(err));
    }
  }

  async function duplicate() {
    setError(null);
    try {
      const { campaignId } = await actions.duplicate.mutateAsync();
      await navigate({ to: '/campaigns/$campaignId', params: { campaignId } });
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <>
      <PageHeader
        title={campaign.name}
        description={
          campaign.status === 'scheduled' && campaign.scheduledAt
            ? `Launches ${when(campaign.scheduledAt)} (your time).`
            : campaign.startedAt
              ? `Launched ${when(campaign.startedAt)}.${campaign.expiresAt && live ? ` Texts not out by ${when(campaign.expiresAt)} are dropped.` : ''}`
              : undefined
        }
        actions={<Back />}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Badge role={status.role} testId="campaign-status">
          {status.label}
        </Badge>
        {campaign.status === 'running' ? (
          <Button
            variant="secondary"
            size="sm"
            busy={actions.pause.isPending}
            onClick={() => void run('pause')}
          >
            Pause
          </Button>
        ) : null}
        {campaign.status === 'paused' ? (
          <Button size="sm" busy={actions.resume.isPending} onClick={() => void run('resume')}>
            Resume
          </Button>
        ) : null}
        {campaign.status === 'scheduled' ? (
          <Button
            variant="secondary"
            size="sm"
            busy={actions.unschedule.isPending}
            onClick={() => void run('unschedule')}
          >
            Unschedule
          </Button>
        ) : null}
        {live || campaign.status === 'scheduled' ? (
          <Button
            variant="danger"
            size="sm"
            busy={actions.cancel.isPending}
            onClick={() => void run('cancel')}
          >
            Cancel campaign
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          busy={actions.duplicate.isPending}
          onClick={() => void duplicate()}
        >
          Duplicate
        </Button>
        {campaign.startedAt ? (
          <Link
            to="/inbox"
            search={{ campaign: campaign.id }}
            className="fl-btn fl-btn--ghost fl-btn--sm focus-visible:outline-2 focus-visible:outline-focus-ring"
          >
            Conversations
          </Link>
        ) : null}
      </div>
      {campaign.status === 'paused' && campaign.pauseReason ? (
        <Alert tone="warning">{PAUSE_REASONS[campaign.pauseReason] ?? 'Paused.'}</Alert>
      ) : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {campaign.status === 'scheduled' ? (
        <Alert tone="info">
          It's checked again when it launches: who gets it, the cost limit and every sending rule.
        </Alert>
      ) : null}

      {stats.isError ? <Alert tone="error">{stats.error.message}</Alert> : null}
      {s ? (
        <Card title="Progress">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="campaign-stats">
            <Stat label="Recipients" value={n(s.recipients)} />
            <Stat label="Sent" value={n(s.sent + s.delivered)} testId="stat-sent" />
            <Stat label="Delivered" value={n(s.delivered)} />
            <Stat label="Failed" value={n(s.failed + s.unknown)} />
            <Stat
              label="Waiting"
              value={n(s.pending + s.queued + s.held + s.sending)}
              testId="stat-waiting"
            />
            <Stat label="Replied" value={n(s.replied)} />
            <Stat label="Opted out" value={n(s.optedOut)} />
            <Stat
              label="Not sent"
              value={n(
                SKIP_REASONS.reduce((sum, r) => sum + s.skipped[r], 0) +
                  s.blocked +
                  s.cancelled +
                  s.expired,
              )}
            />
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {s.cancelled + s.expired + s.blocked > 0 ? (
              <p className="footnote text-ink-muted">
                {s.cancelled > 0 ? `${n(s.cancelled)} cancelled. ` : ''}
                {s.expired > 0 ? `${n(s.expired)} not sent in time. ` : ''}
                {s.blocked > 0 ? `${n(s.blocked)} stopped at sending (opted out, landline…).` : ''}
              </p>
            ) : null}
            <SkipList counts={s.skipped} />
          </div>
        </Card>
      ) : null}

      <Card title="Message">
        <div className="fl-bubble fl-bubble--auto self-start whitespace-pre-wrap">
          {campaign.template}
          {campaign.footer ? `\n${campaign.footer}` : ''}
        </div>
      </Card>

      {campaign.startedAt ? <Recipients campaignId={campaign.id} live={live} /> : null}
    </>
  );
}

const RECIPIENT_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Texted' },
  { value: 'skipped', label: 'Skipped' },
] as const;

function Recipients({ campaignId, live }: { campaignId: string; live: boolean }) {
  const { workspaceId } = useWorkspaceClaims();
  const [status, setStatus] = useState<'all' | 'queued' | 'skipped'>('all');
  const [page, setPage] = useState(0);
  const list = useRecipientsQuery(workspaceId, campaignId, { status, page }, live);
  const pages = Math.max(Math.ceil((list.data?.total ?? 0) / RECIPIENT_PAGE), 1);
  return (
    <Card title="Recipients">
      <div className="flex flex-col gap-3">
        <div className="fl-segmented self-start" role="radiogroup" aria-label="Show recipients">
          {RECIPIENT_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={status === f.value}
              className="fl-segmented__opt"
              onClick={() => {
                setStatus(f.value);
                setPage(0);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {list.isPending ? <Spinner label="Loading recipients" /> : null}
        {list.isError ? <Alert tone="error">{list.error.message}</Alert> : null}
        {list.data?.rows.length === 0 ? <EmptyState title="Nobody here" /> : null}
        {list.data && list.data.rows.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline" data-testid="recipients">
            {list.data.rows.map((r) => {
              const detail =
                r.status === 'skipped'
                  ? (SKIP_REASON_LABELS[r.skipReason as SkipReason] ?? 'Skipped')
                  : r.status === 'pending'
                    ? 'Not queued yet'
                    : r.messageStatus
                      ? statusLabel(r.messageStatus as MessageStatus).label
                      : 'Queued';
              const reason = r.messageReason ? describeReason(r.messageReason) : null;
              return (
                <li
                  key={r.merchantId}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <Link
                    to="/inbox/$merchantId"
                    params={{ merchantId: r.merchantId }}
                    className="callout min-w-0 truncate text-ink underline-offset-2 hover:underline"
                  >
                    {r.label}
                  </Link>
                  <span className="footnote text-ink-muted" title={reason ?? undefined}>
                    {detail}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
        {pages > 1 ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="footnote text-ink-muted">
              Page {page + 1} of {pages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page + 1 >= pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
